declare global {
  interface Window {
    /** Instance of Usercentrics (which is probably empty and useless) */
    UC_UI: any;
  }
}

export const usercentricsUpdateEventName = 'UcUpdateEvent';

/**
 * Usercentrics v2 CMP integration. This is NOT Cookiebot. Instantiate this class only once. Does not work with v3, only v2!
 * @example
 *   // Create instance (only once)
 *   window.fvUsercentrics = new UsercentricsCmp();
 *
 *   // Check consent
 *   window.fvUsercentrics.getConsent('marketing'); // true | false
 *
 *   // Listen for changes
 *   document.addEventListener(usercentricsUpdateEventName, () => {
 *     // consent has changed
 *   })
 */
export class UsercentricsCmp {
  /** Show console logs. Set to `false` on production environment by default. */
  debug: boolean;

  /** On {@link init} Usercentrics might not have been initiated. We try again after a timeout. Time in milliseconds */
  timeout: number;

  constructor() {
    // set debug to `true` on non-production environments
    this.debug =
      document.documentElement.getAttribute('data-env') !== 'production';

    this.timeout = 500;

    /** Let's go! */
    this.load();
  }

  /** Elements that inform the user about content that is being blocked. Instances of the friendation component BasisCookieConsentNotification. */
  get notifications() {
    const items: HTMLElement[] = Array.from(
      document.querySelectorAll('[is="atom-cookie-consent-notice"]')
    );
    return items;
  }

  /** Ucercentrics loads itself because there is a `<scipt>` tag in the DOM – we may need to wait a bit before we can run {@link init} and apply any changes/event listeners with JavaScript */
  private load() {
    /** Maximum tries allowed */
    const max = 3;

    /** Count the tries */
    let count = 0;

    /** Try to identify Usercentrics */
    const tryAgain = () => {
      if (!window.UC_UI) {
        if (this.debug) console.warn('Usercentrics is not yet initialized');

        setTimeout(() => {
          /** Limit the amount of times we try to {@link max} tries */
          count++;
          if (count <= max) {
            if (this.debug)
              console.info(
                `Trying to identify Usercentrics again after ${this.timeout}ms`
              );
            /** Try again */
            tryAgain();
          } else {
            if (this.debug)
              console.info(
                `Failed to identify Usercentrics after trying ${max} times every ${this.timeout}ms.`
              );
          }
        }, this.timeout);
      } else {
        if (this.debug)
          console.info(
            `✅ Usercentrics is initialized and available at window.UC_UI`
          );

        this.init();
      }
    };

    tryAgain();
  }

  /** Fire a custom `Event` to indicate that the Ucercentrics settings have changed in some way or the user has at least interacted with the cookie banner. We can listen to this event in other components with:
   * @example
   *   document.addEventListener(usercentricsUpdateEventName, () => {
   *     poster.inert = !window.fvUsercentrics.getConsent('marketing')
   *   });
   */
  private fireUpdateEvent() {
    if (this.debug)
      console.info('User interaction with Usercentrics registered');

    this.toggleContent();
    this.toggleNotifications();

    const UcUpdateEvent = new Event(usercentricsUpdateEventName);
    document.dispatchEvent(UcUpdateEvent);
  }

  private init() {
    /**
     * Interactions with {@link triggers} will cause the cookie banner to open
     */
    const bindEventsOnTriggers = () => {
      const triggers: HTMLAnchorElement[] = Array.from(
        document.querySelectorAll('a[href*="#cookie-settings"]')
      );

      /** Listen for clicks on links that would open the cookie settings */
      triggers.forEach((trigger) => {
        const onClick = (e: MouseEvent) => {
          e.preventDefault();
          if (this.debug) console.info('Requesting cookie settings overlay');

          // Open the cookie banner on level 2. You can also open it on level 1 with `window.UC_UI.showSecondLayer()`
          window.UC_UI.showSecondLayer();
        };

        trigger.addEventListener('click', onClick.bind(this));
      });
    };

    /** Most user interactions with Ucercentrics trigger this event. Because the name is so complicated, we create a new custom event in {@link userInteractionWithUsercentrics} */
    window.addEventListener('UC_UI_CMP_EVENT', this.fireUpdateEvent.bind(this));

    bindEventsOnTriggers();
    this.fireUpdateEvent();

    if (this.debug) console.info('Usercentrics (window.UC_UI)', window.UC_UI);
  }

  /**
   * Hide/show blocked content based on the current consent
   */
  private toggleContent() {
    const items: HTMLElement[] = Array.from(
      document.querySelectorAll('[data-usercentrics]')
    );

    /** All the elements except script tags. Script tags are handled by Usercentrics automatically */
    const elements = items.filter((x) => x.nodeName !== 'SCRIPT');

    elements.forEach((element) => {
      const categorySlug = element.getAttribute('data-usercentrics');

      if (this.debug) {
        console.info(
          `An element is linked to '${categorySlug}' (consent: ${this.getConsent(
            categorySlug
          )})`,
          element
        );
      }

      if (categorySlug) element.inert = !this.getConsent(categorySlug);
    });
  }

  /**
   * Hide/show blocked elements based on the given consent
   */
  private toggleNotifications() {
    if (this.debug) console.info('toggleNotifications');

    this.notifications.forEach((notification) => {
      if (notification.hasAttribute('data-category-slug')) {
        /** Hide content blocker because the consent has been given */
        notification.inert = this.getConsent(
          notification.getAttribute('data-category-slug')
        );
      }
    });
  }

  /**
   * Check the status of a {@link category} by matching the `categorySlug` (like `"functional"`) or the `name` (like `"Google Analytics"` or `"epilot"`).
   */
  public getConsent(categorySlug: string): boolean {
    return window.UC_UI?.getServicesBaseInfo().find(
      (x) =>
        x.categorySlug.toLowerCase() === categorySlug.toLowerCase() ||
        x.name.toLowerCase() === categorySlug.toLowerCase()
    )?.consent?.status;
  }
}
